import { useMemo } from "react";
import { motion } from "framer-motion";

// Top-down floor plan of MarketPlace on Oakton, transcribed 1:1 from the owner's
// hand-drawn layout. Pure SVG so the layout is pixel-exact (no AI guessing).
// viewBox is landscape (matches the paper). Entrance/Exit are on the front (bottom) wall.

const COL = {
  deli: "#FF9F0A",
  meat: "#FF375F",
  grocery: "#0A84FF",
  produce: "#34C759",
  dairy: "#64D2FF",
  frozen: "#5AC8FA",
  cashier: "#BF5AF2",
  office: "#8E8E93",
  goods: "#48484A",
  utility: "#636366",
};

// Each region carries the camera-zone tokens it represents, so a person's trail
// lights up the right part of the store.
const REGIONS = [
  // ---- BACK WALL (top) ----
  { x: 70, y: 34, w: 170, h: 96, label: "DELI", c: COL.deli, zones: ["deli"] },
  { x: 250, y: 34, w: 205, h: 96, label: "MEAT & BUTCHER", sub: "Cut Fresh Meat", c: COL.meat, zones: ["meat", "butcher", "meat isle"] },
  { x: 457, y: 34, w: 22, h: 96, label: "SLICER", vlabel: true, c: COL.meat, zones: [] },
  { x: 496, y: 34, w: 300, h: 70, label: "PRE-PACKAGED CHICKEN · VEAL · BEEF", c: COL.meat, zones: ["chicken", "poultry"] },
  { x: 900, y: 34, w: 80, h: 118, label: "CUT FRUIT / NUTS", c: COL.produce, zones: ["nuts", "dried nuts", "grape table"] },

  // ---- LEFT WALL (vertical) ----
  { x: 16, y: 150, w: 44, h: 150, label: "DAIRY & EGGS", vlabel: true, c: COL.dairy, zones: ["dairy", "eggs", "yogurt"] },
  { x: 62, y: 150, w: 44, h: 150, label: "FROZEN", vlabel: true, c: COL.frozen, zones: ["frozen", "cooler"] },

  // Prepared food (under deli)
  { x: 118, y: 150, w: 122, h: 44, label: "PREPARED FOOD", c: COL.deli, zones: ["prepared"] },
  { x: 112, y: 198, w: 32, h: 100, label: "SAUSAGE & CHEESE", vlabel: true, small: true, c: COL.deli, zones: [] },

  // ---- GROCERY AISLES 1–5 (upper) ----
  { aisle: true, x: 150, y: 196, w: 320, h: 104, header: "GROCERY AISLES", cols: ["5", "4", "3", "2", "1"], c: COL.grocery, zones: ["grocery", "aisle", "cereal", "spices", "coffee", "tea", "chocolate", "pastry", "bread", "chips", "oil"] },

  // ---- RIGHT UPPER ----
  { x: 560, y: 150, w: 54, h: 60, label: "OIL", c: COL.grocery, zones: ["oil"] },
  { x: 625, y: 150, w: 175, h: 38, label: "RICE", c: COL.grocery, zones: ["rice"] },
  { x: 625, y: 196, w: 150, h: 40, label: "PRODUCE", c: COL.produce, zones: ["produce", "soft fruit"] },
  { x: 818, y: 170, w: 72, h: 54, label: "PRODUCE", c: COL.produce, zones: ["produce"] },
  { x: 818, y: 234, w: 72, h: 60, label: "PRODUCE", c: COL.produce, zones: ["produce"] },
  { x: 900, y: 170, w: 80, h: 230, label: "PRODUCE", vlabel: true, c: COL.produce, zones: ["produce", "produce cooler", "produce prep"] },

  // ---- PATHWAY divider ----
  { pathway: true, x: 150, y: 308, w: 650, h: 26 },
  { x: 540, y: 196, w: 22, h: 104, label: "BEANS", vlabel: true, c: COL.grocery, zones: ["beans"] },
  { x: 600, y: 340, w: 130, h: 56, label: "PRODUCE", c: COL.produce, zones: ["produce"] },

  // ---- OFFICE ----
  { x: 16, y: 344, w: 90, h: 128, label: "OFFICE", c: COL.office, zones: ["office", "office door"] },

  // ---- CHECKOUT — REGISTERS 1–5 (lower) ----
  { x: 118, y: 344, w: 26, h: 128, label: "DRINKS COOLER", vlabel: true, small: true, c: COL.frozen, zones: ["drinks"] },
  { aisle: true, x: 154, y: 344, w: 316, h: 128, header: "CHECKOUT · REGISTERS", cols: ["5", "4", "3", "2", "1"], c: COL.cashier, zones: ["cashier", "checkout", "register", "reg", "all registers"] },

  // ---- FRONT AREA (bottom) ----
  { x: 16, y: 500, w: 90, h: 148, label: "ICE MAKER", sub: "Ice Water", c: COL.utility, zones: ["ice"] },
  { x: 154, y: 500, w: 316, h: 44, label: "GOODS", c: COL.goods, zones: ["goods"] },
  { x: 760, y: 150, w: 0, h: 0 }, // spacer (no-op, keeps indices readable)
  { x: 600, y: 470, w: 150, h: 56, label: "STORE ENTRANCE", c: COL.produce, border: true, zones: ["store entrance"] },
  { x: 762, y: 440, w: 128, h: 60, label: "PRODUCE", c: COL.produce, zones: ["produce"] },
  { x: 762, y: 512, w: 128, h: 64, label: "GOODS", c: COL.goods, zones: ["goods"] },
  { x: 900, y: 430, w: 80, h: 218, label: "CABLE", vlabel: true, c: COL.utility, zones: ["cable"] },
  { x: 154, y: 596, w: 236, h: 44, label: "GOODS", c: COL.goods, zones: ["goods"] },

  // doors on the front wall
  { door: "exit", x: 430, y: 600, w: 120, h: 40, label: "EXIT", c: "#FF375F", zones: ["exit", "exiting"] },
  { door: "entrance", x: 590, y: 600, w: 170, h: 40, label: "ENTRANCE", c: "#34C759", zones: ["entrance", "berri"] },
];

const norm = (s) => (s || "").toString().toLowerCase();

// Resolve a camera-zone token to the centre of its region on the floor plan,
// so a person's sightings can be drawn as an actual path across the store.
function zonePoint(zoneStr) {
  const z = norm(zoneStr);
  if (!z) return null;
  for (const r of REGIONS) {
    if (!r.w || !r.h || !r.zones || !r.zones.length) continue;
    if (r.zones.some((rz) => z.includes(rz) || rz.includes(z))) {
      return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
    }
  }
  return null;
}

export default function StoreFloorPlan({ activeZones = [], currentZone = "", trail = [], trailProgress = 1, className = "" }) {
  const active = activeZones.map(norm);
  const cur = norm(currentZone);
  const isActive = (zones) => zones.some((z) => active.some((a) => a.includes(z) || z.includes(a)));
  const isCurrent = (zones) => !!cur && zones.some((z) => cur.includes(z) || z.includes(cur));

  // Build the walk path: map each sighting zone to a floor-plan point, in order.
  const path = useMemo(() => {
    const pts = [];
    let prev = null;
    for (const z of trail) {
      const p = zonePoint(z);
      if (!p) continue;
      if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < 2) continue; // skip staying put
      pts.push({ ...p, zone: z });
      prev = p;
    }
    const segs = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const len = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      segs.push(len); total += len;
    }
    return { pts, segs, total };
  }, [trail]);

  const prog = Math.max(0, Math.min(1, trailProgress));
  const head = useMemo(() => {
    const { pts, segs, total } = path;
    if (!pts.length) return null;
    if (pts.length === 1 || total === 0) return pts[0];
    let want = prog * total;
    for (let i = 0; i < segs.length; i++) {
      if (want <= segs[i] || i === segs.length - 1) {
        const f = segs[i] ? want / segs[i] : 0;
        return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * f, y: pts[i].y + (pts[i + 1].y - pts[i].y) * f };
      }
      want -= segs[i];
    }
    return pts[pts.length - 1];
  }, [path, prog]);
  const trailD = path.pts.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");

  return (
    <div className={`rounded-2xl bg-[#101013] border border-white/10 p-4 ${className}`} data-testid="store-floorplan">
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-xs uppercase tracking-widest text-[#71717A]">Store floor plan · MarketPlace on Oakton</p>
        <div className="flex items-center gap-3 text-[11px] text-[#71717A]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#34C759]" /> Entrance</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF375F]" /> Exit</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0A84FF]" /> Visited</span>
        </div>
      </div>
      <svg viewBox="0 0 1000 680" className="w-full h-auto select-none" style={{ maxHeight: 520 }}>
        {/* store outer wall */}
        <rect x="12" y="12" width="976" height="656" rx="14" fill="#0A0A0C" stroke="#2A2A2E" strokeWidth="2.5" />

        {REGIONS.map((r, i) => {
          if (!r.w || !r.h) return null;

          if (r.pathway) {
            return (
              <g key={i}>
                <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="6" fill="#141418" stroke="#2A2A2E" strokeDasharray="4 5" />
                <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 4} textAnchor="middle" fontSize="12" letterSpacing="3" fill="#6B6B70">⟵  PATHWAY  ⟶</text>
              </g>
            );
          }

          if (r.aisle) {
            const cw = r.w / r.cols.length;
            const on = isActive(r.zones);
            const now = isCurrent(r.zones);
            return (
              <g key={i}>
                <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="8"
                  fill={on ? `${r.c}22` : "#16161A"} stroke={on ? r.c : "#2E2E33"} strokeWidth={now ? 2.5 : 1.5} />
                {r.cols.map((c, ci) => (
                  <g key={ci}>
                    {ci > 0 && <line x1={r.x + cw * ci} y1={r.y + 6} x2={r.x + cw * ci} y2={r.y + r.h - 6} stroke="#2E2E33" strokeWidth="1" />}
                    <text x={r.x + cw * ci + cw / 2} y={r.y + r.h / 2 + 10} textAnchor="middle" fontSize="26" fontWeight="700" fill={on ? r.c : "#3A3A3F"}>{c}</text>
                  </g>
                ))}
                <text x={r.x + r.w / 2} y={r.y + 16} textAnchor="middle" fontSize="10" letterSpacing="2" fontWeight="600" fill={on ? r.c : "#6B6B70"}>{r.header}</text>
                {now && <AisleDot x={r.x + r.w / 2} y={r.y + r.h / 2} c={r.c} />}
              </g>
            );
          }

          const on = isActive(r.zones || []);
          const now = isCurrent(r.zones || []);
          const cx = r.x + r.w / 2;
          const cy = r.y + r.h / 2;
          return (
            <g key={i}>
              <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="7"
                fill={on ? `${r.c}26` : "#16161A"}
                stroke={r.door || r.border ? r.c : (on ? r.c : "#2E2E33")}
                strokeWidth={now ? 2.5 : (r.door || r.border ? 2 : 1.3)}
                strokeDasharray={r.door ? "6 4" : "none"} />
              {r.vlabel ? (
                <text x={cx} y={cy} textAnchor="middle" fontSize={r.small ? 9 : 10} fontWeight="600"
                  fill={on ? r.c : "#8A8A90"} transform={`rotate(-90 ${cx} ${cy})`} letterSpacing="1">{r.label}</text>
              ) : (
                <>
                  <text x={cx} y={r.sub ? cy - 3 : cy + 4} textAnchor="middle"
                    fontSize={r.small ? 9 : (r.w < 80 ? 9 : 11)} fontWeight="700" letterSpacing="0.5"
                    fill={on ? r.c : (r.door ? r.c : "#B5B5BA")}>{r.label}</text>
                  {r.sub && <text x={cx} y={cy + 12} textAnchor="middle" fontSize="8.5" fill="#6B6B70">{r.sub}</text>}
                </>
              )}
              {now && <AisleDot x={cx} y={cy} c={r.c} />}
            </g>
          );
        })}

        {/* Animated walk trail — the person's day drawn as a path across the store */}
        {path.pts.length > 1 && (
          <g data-testid="movement-trail">
            <path d={trailD} fill="none" stroke="#0A84FF" strokeWidth="3.5"
              strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray={path.total} strokeDashoffset={path.total * (1 - prog)}
              style={{ filter: "drop-shadow(0 0 5px rgba(10,132,255,0.85))" }} />
            {path.pts.map((p, i) => (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r="8.5" fill="#0A0A0C" stroke="#0A84FF" strokeWidth="2" />
                <text x={p.x} y={p.y + 3.5} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#0A84FF">{i + 1}</text>
              </g>
            ))}
            {head && (
              <g>
                <motion.circle cx={head.x} cy={head.y} r="11" fill="#0A84FF"
                  animate={{ r: [11, 17, 11], opacity: [0.35, 0.08, 0.35] }}
                  transition={{ repeat: Infinity, duration: 1.4 }} />
                <circle cx={head.x} cy={head.y} r="6.5" fill="#fff" stroke="#0A84FF" strokeWidth="3" />
              </g>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}

function AisleDot({ x, y, c }) {
  return (
    <g>
      <motion.circle cx={x} cy={y} r="9" fill={c}
        animate={{ r: [9, 16, 9], opacity: [0.9, 0.25, 0.9] }} transition={{ repeat: Infinity, duration: 1.6 }} />
      <circle cx={x} cy={y} r="5.5" fill="#fff" />
    </g>
  );
}
